import React from 'react';
import PropTypes from 'prop-types';
import { kebabCase } from 'lodash';
import * as Core from '../core';
import { Button } from './Button';
import { getActiveRuleSetFromUrl } from '../utils/UrlUtil';
import { i18n } from '../i18n/translate';

const MENU_WIDTH = '300px';

export const HeaderMenuAppSwitcher = ({ currentRuleSets = [], menuItems = [], onRedirect }) => {
  const { t } = i18n;

  const APP_OPTIONS = Object.freeze([
    {
      slug: 'train',
      group: 'Train',
      label: t('Train'),
      details: t('Assign and manage learning'),
      icon: 'building-shield',
    },
    {
      slug: 'learn',
      group: 'Learn',
      label: t('Learn'),
      details: t('Complete training assigned to you'),
      icon: 'puzzle',
    },
    {
      slug: 'impact',
      group: 'Impact',
      label: t('Impact'),
      details: t('View the impact of initiatives'),
      icon: 'bullseye-arrow',
    },
    {
      slug: 'promote',
      group: 'Promote',
      label: t('Promote'),
      details: t('Manage marketing programs'),
      icon: 'flag-checkered',
    },
    {
      slug: 'engage',
      group: 'Engage',
      label: t('Engage'),
      details: t('Manage webinars and in-person events'),
      icon: 'microchip',
    },
    {
      slug: 'workshop',
      group: 'Workshop',
      label: t('Engage'),
      details: t('Manage webinars and in-person events'),
      icon: 'microchip',
    },
  ]);

  const activeRuleSet = getActiveRuleSetFromUrl(currentRuleSets);
  const availableApps = APP_OPTIONS.filter(appOption => menuItems.some(menuItem => menuItem.slug === appOption.slug));
  const defaultApp = availableApps.find(appOption => appOption.group === activeRuleSet?.interstitial_group);

  // no appswitcher if there is not more that one train role to choose from
  if (availableApps.length < 2) {
    return null;
  }

  const items = availableApps.map(appOption => ({
    ariaLabel: appOption.label,
    text: (
      <Core.Layout variant="flex" alignItems="center" gap="xs">
        <Core.Icon prefix="far" iconName={appOption.icon} />
        <Core.Layout>
          <Core.Typography mb={0} textStyle="ui-default">
            {appOption.label}
          </Core.Typography>
          <Core.Typography mb={0} textStyle="ui-small" color="text-subdued">
            {appOption.details}
          </Core.Typography>
        </Core.Layout>
      </Core.Layout>
    ),
    value: appOption.group,
    closeOnSelect: true,
    dataAction: `select-option-${kebabCase(appOption.label)}`,
    dataObjectId: appOption.slug,
    dataObjectType: 'menu-dropdown-options',
  }));

  const onChange = nextValue => {
    const selectedApp = availableApps.find(appOption => appOption.group === nextValue);
    const menuItem = selectedApp && menuItems.find(menuItem => menuItem.slug === selectedApp.slug);
    if (menuItem) {
      onRedirect(menuItem.target_url);
    }
  };

  return (
    <div className="header-menu-app-switcher">
      <Core.DropdownMenu
        groups={[{ items, menuGroupOptionType: 'radio', value: defaultApp?.group ?? '', onChange }]}
        isLazy
        isMenuItemOptions
        menuButton={
          <Button icon={defaultApp?.icon} label={defaultApp?.label} rightIcon="angle-down" style="elevated" />
        }
        menuListWidth={MENU_WIDTH}
        placement="bottom-end"
      />
    </div>
  );
};

HeaderMenuAppSwitcher.propTypes = {
  currentRuleSets: PropTypes.arrayOf(
    PropTypes.shape({
      active: PropTypes.bool,
      interstitial_group: PropTypes.string,
      name: PropTypes.string,
    })
  ),
  menuItems: PropTypes.arrayOf(
    PropTypes.shape({
      slug: PropTypes.string,
      target_url: PropTypes.string,
    })
  ),
  onRedirect: PropTypes.func,
};
